import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useCallback, useEffect, useMemo, useState } from "react";
import { foyerApi, type ScreenRecord } from "../http/foyer";
import { tokens } from "../theme";
import { describeError, useToast } from "./toast";
import { Trans, useTranslation } from "react-i18next";
import type {
  ProvisioningResult,
  ProvisioningSeatSummary,
} from "@shared/types/foyer";
import { roleLabel } from "../utils/devicePresentation";
import type { Translate } from "../i18n/translate";
import { errorText } from "../utils/apiError";

interface Props {
  open: boolean;
  premiseId: string;
  rooms: { id: string; name?: string }[];
  /** Whether the door-sign role is offered - true only on care premises. Hotels
   *  never see the role picker/column (every pre-seeded screen is a TV). */
  allowDoorSign?: boolean;
  onClose: () => void;
  /** Called after a successful add/import/remove so the parent can refresh. */
  onChanged?: () => void;
}

/** Human-readable copy for a provisioning error reason code. */
function reasonLabel(t: Translate, reason: string): string {
  switch (reason) {
    case "missing_serial":
      return t("premises:provisioningDialog.reasons.missingSerial");
    case "missing_room":
      return t("premises:provisioningDialog.reasons.missingRoom");
    case "unknown_room":
      return t("premises:provisioningDialog.reasons.unknownRoom");
    case "room_already_taken":
      return t("premises:provisioningDialog.reasons.roomAlreadyTaken");
    case "duplicate_serial":
      return t("premises:provisioningDialog.reasons.duplicateSerial");
    case "serial_in_use_elsewhere":
      return t("premises:provisioningDialog.reasons.serialInUseElsewhere");
    case "no_free_licenses":
      return t("premises:provisioningDialog.reasons.noFreeLicenses");
    default:
      return reason;
  }
}

export default function ProvisioningDialog({
  open,
  premiseId,
  rooms,
  allowDoorSign = false,
  onClose,
  onChanged,
}: Props) {
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);
  const [screens, setScreens] = useState<ScreenRecord[]>([]);
  const [seats, setSeats] = useState<ProvisioningSeatSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [serial, setSerial] = useState("");
  const [roomId, setRoomId] = useState("");
  const [model, setModel] = useState("");
  const [adding, setAdding] = useState(false);

  const [importing, setImporting] = useState(false);
  const [lastResult, setLastResult] = useState<ProvisioningResult | null>(null);

  const load = useCallback(async () => {
    if (!premiseId) return;
    try {
      const prov = await foyerApi.provisioningList(premiseId);
      setScreens(prov.screens);
      setSeats(prov.seats);
      setError(null);
    } catch (err) {
      setError(errorText(err, t));
    }
  }, [premiseId, t]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const takenRoomIds = useMemo(
    () => new Set(screens.filter((s) => s.roomId).map((s) => s.roomId)),
    [screens]
  );
  const freeRooms = useMemo(
    () => rooms.filter((r) => !takenRoomIds.has(r.id)),
    [rooms, takenRoomIds]
  );

  const roomLabel = useCallback(
    (id: string | undefined) => {
      if (!id) return "-";
      const r = rooms.find((x) => x.id === id);
      return r?.name ? r.name : id;
    },
    [rooms]
  );

  const awaiting = useMemo(
    () => screens.filter((s) => s.provisioned),
    [screens]
  );

  const refreshAll = useCallback(async () => {
    await load();
    onChanged?.();
  }, [load, onChanged]);

  const addOne = useCallback(async () => {
    if (!serial.trim() || !roomId) return;
    setAdding(true);
    setError(null);
    try {
      const result = await foyerApi.provisionDevice(premiseId, {
        serial: serial.trim(),
        room: roomId,
        model: model.trim() || undefined,
      });
      setLastResult(result);
      toast.success(
        t("provisioningDialog.preseeded", {
          serial: serial.trim(),
          room: roomId,
        })
      );
      setSerial("");
      setRoomId("");
      setModel("");
      await refreshAll();
    } catch (err) {
      const code = (err as { code?: string })?.code;
      const why = code ? reasonLabel(t, code) : errorText(err, t);
      setError(why);
      toast.error(
        t("provisioningDialog.preseedFailed", {
          serial: serial.trim(),
          reason: String(why).replace(/[.\s]+$/, ""),
        })
      );
    } finally {
      setAdding(false);
    }
  }, [premiseId, serial, roomId, model, refreshAll, toast, t]);

  const importCsv = useCallback(
    async (file: File | null | undefined) => {
      if (!file) return;
      setImporting(true);
      setError(null);
      try {
        const result = await foyerApi.provisionImport(premiseId, file);
        setLastResult(result);
        toast.success(t("provisioningDialog.imported", { name: file.name }));
        await refreshAll();
      } catch (err) {
        setError(errorText(err, t));
        toast.error(describeError(t, "importFile", err, { name: file.name }));
      } finally {
        setImporting(false);
      }
    },
    [premiseId, refreshAll, toast, t]
  );

  const removeOne = useCallback(
    async (screenId: string) => {
      if (!window.confirm(t("provisioningDialog.removeConfirm"))) return;
      try {
        await foyerApi.deleteProvisioned(premiseId, screenId);
        toast.success(t("provisioningDialog.removed"));
        await refreshAll();
      } catch (err) {
        setError(errorText(err, t));
        toast.error(describeError(t, "removePreseededDevice", err));
      }
    },
    [premiseId, refreshAll, toast, t]
  );

  const seatLabel = seats
    ? seats.unlimited
      ? t("provisioningDialog.seatsUnlimited", {
          count: seats.activatedCount,
        })
      : t("provisioningDialog.seatsUsed", {
          used: seats.activatedCount,
          total: seats.totalSeats,
        })
    : "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6">
              {t("provisioningDialog.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("provisioningDialog.subtitle")}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label={t("common:buttons.close")}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}

          {seats && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: "wrap", gap: 1 }}
            >
              <Chip
                label={t("provisioningDialog.awaitingCount", {
                  count: awaiting.length,
                })}
                sx={{ bgcolor: tokens.warningTint, color: tokens.warning }}
              />
              <Chip
                label={seatLabel}
                color={
                  !seats.unlimited && !seats.hasFreeSeats ? "error" : "default"
                }
              />
            </Stack>
          )}

          {/* Manual add */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            sx={{ alignItems: { md: "flex-start" } }}
          >
            <TextField
              size="small"
              label={t("provisioningDialog.serialNumber")}
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              sx={{ minWidth: 180 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="prov-room">
                {t("provisioningDialog.unit")}
              </InputLabel>
              <Select
                labelId="prov-room"
                label={t("provisioningDialog.unit")}
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
              >
                {freeRooms.length === 0 && (
                  <MenuItem value="" disabled>
                    {t("provisioningDialog.noFreeUnits")}
                  </MenuItem>
                )}
                {freeRooms.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name ? `${r.name} (${r.id})` : r.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label={t("provisioningDialog.modelOptional")}
              value={model}
              onChange={(e) => setModel(e.target.value)}
              sx={{ minWidth: 160 }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => void addOne()}
              disabled={adding || !serial.trim() || !roomId}
            >
              {t("common:buttons.add")}
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={importing}
            >
              {importing
                ? t("provisioningDialog.importing")
                : t("provisioningDialog.importCsv")}
              <input
                type="file"
                hidden
                accept=".csv,text/csv,text/plain"
                onChange={(e) => {
                  void importCsv(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            <Trans
              t={t}
              i18nKey="provisioningDialog.csvHelp"
              components={{ code: <code /> }}
            />
          </Typography>

          {lastResult && (
            <Alert
              severity={lastResult.errors.length > 0 ? "warning" : "success"}
              onClose={() => setLastResult(null)}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: "fontWeightSemibold" }}
              >
                {t("provisioningDialog.provisionedCount", {
                  count: lastResult.created.length,
                })}
                {lastResult.errors.length > 0
                  ? t("provisioningDialog.skippedCount", {
                      n: lastResult.errors.length,
                    })
                  : ""}
              </Typography>
              {lastResult.errors.length > 0 && (
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {lastResult.errors.slice(0, 12).map((e, i) => (
                    <li key={i}>
                      <Typography variant="caption">
                        {e.row
                          ? t("provisioningDialog.rowPrefix", { row: e.row })
                          : ""}
                        {e.serial ? `${e.serial} - ` : ""}
                        {reasonLabel(t, e.reason)}
                      </Typography>
                    </li>
                  ))}
                  {lastResult.errors.length > 12 && (
                    <li>
                      <Typography variant="caption">
                        {t("provisioningDialog.more", {
                          n: lastResult.errors.length - 12,
                        })}
                      </Typography>
                    </li>
                  )}
                </Box>
              )}
            </Alert>
          )}

          {/* Awaiting-activation list */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("provisioningDialog.awaiting")}
            </Typography>
            {awaiting.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("provisioningDialog.awaitingEmpty")}
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("provisioningDialog.unit")}</TableCell>
                    {allowDoorSign && (
                      <TableCell>{t("provisioningDialog.role")}</TableCell>
                    )}
                    <TableCell>{t("provisioningDialog.serial")}</TableCell>
                    <TableCell>{t("provisioningDialog.model")}</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {awaiting.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell>{roomLabel(s.roomId)}</TableCell>
                      {allowDoorSign && (
                        <TableCell>
                          <Chip
                            size="small"
                            label={roleLabel(t, s.role)}
                            color={
                              s.role === "door-sign" ? "secondary" : "default"
                            }
                            variant={
                              s.role === "door-sign" ? "filled" : "outlined"
                            }
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "fontFamilyMono" }}
                        >
                          {s.serial || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>{s.model || "-"}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => void removeOne(s.id)}
                        >
                          {t("common:buttons.remove")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
